import { createSelector } from '@reduxjs/toolkit';

import { UserRole } from 'enums';
import {
  checkIsMultiAccount,
  getUserRoles,
  isAdminRole,
} from 'utils/userRolesUtils';
import { RootState } from 'redux/reducers/rootReducer';

const authSelector = (state: RootState) => state.auth;

const userSelector = (state: RootState) => state.auth.user;

const isAuthenticatedSelector = createSelector(userSelector, (user) => !!user);

const activeRoleSelector = (state: RootState) => state.auth.activeRole;

const isAdminSelector = createSelector(authSelector, isAdminRole);

const isCorporateTASelector = createSelector(authSelector, (auth) => {
  if (checkIsMultiAccount(auth?.user?.roles))
    return auth?.activeRole === UserRole.CorporateTA;

  return auth?.user?.roles?.some((role) => role === UserRole.CorporateTA);
});

const isRecruiterCompanySelector = createSelector(authSelector, (auth) => {
  if (checkIsMultiAccount(auth?.user?.roles))
    return auth?.activeRole === UserRole.RecruiterCompany;

  return auth?.user?.roles?.some((role) => role === UserRole.RecruiterCompany);
});

const isTeammateAdminSelector = createSelector(
  userSelector,
  (user) => user?.roles?.some((role) => role === UserRole.TeammateAdmin),
);

const isTeammateSubAdminSelector = createSelector(
  userSelector,
  (user) => user?.roles?.some((role) => role === UserRole.TeammateSubAdmin),
);

const isTeammateAdminLevelSelector = createSelector(
  userSelector,
  (user) =>
    user?.roles?.some(
      (role) =>
        role === UserRole.TeammateSubAdmin || role === UserRole.TeammateAdmin,
    ),
);

const isTeammateSelector = createSelector(
  userSelector,
  (user) =>
    user?.roles?.some(
      (role) => getUserRoles(UserRole.Teammates).indexOf(role as UserRole) >= 0,
    ),
);

const bullhornEnableSelector = (state: RootState) =>
  state.auth.user?.bullhornEnable;

const hasRequiredRoleSelector = (roles: UserRole[]) =>
  createSelector(authSelector, (auth) => {
    if (checkIsMultiAccount(auth.user?.roles)) {
      return roles.includes(auth.activeRole as UserRole);
    }

    return auth.user?.roles?.some(
      (role) => roles.indexOf(role as UserRole) >= 0,
    );
  });

const isMultiAccountSelector = createSelector(
  userSelector,
  (user) =>
    user?.roles?.includes(UserRole.Admin) &&
    user?.roles?.includes(UserRole.User),
);

const isAdminAccessDeniedSelector = createSelector(
  userSelector,
  (user) =>
    !user?.isBOTwoFactorAuthStatus && user?.roles?.includes(UserRole.Admin),
);

export {
  userSelector,
  isAdminSelector,
  isAuthenticatedSelector,
  hasRequiredRoleSelector,
  isCorporateTASelector,
  isRecruiterCompanySelector,
  isTeammateAdminSelector,
  isTeammateSelector,
  bullhornEnableSelector,
  isTeammateSubAdminSelector,
  isMultiAccountSelector,
  authSelector,
  activeRoleSelector,
  isAdminAccessDeniedSelector,
  isTeammateAdminLevelSelector,
};
